import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { ContentItemInputBuilder } from 'components/WorkspaceEditor/factories/ContentItemInputBuilder'
import { SpecifierNotesInputBuilder } from 'components/WorkspaceEditor/factories/SpecifierNotesInputBuilder'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useNodeMapActions } from '../features/keyOperations/store/NodeMapStore'

/**
 * Service to handle content mutations
 * @returns {Object} - Object containing mutation functions
 */
export const useContentMutationService = () => {
    /** Mutation function to update the content item elements */
    const {
        updateProjectContentItemElement,
        mutationInitialActiveTabRef,
    } = useProjectContentItemElementsMutation()
    /** selected project element node id from global store */
    const {
        contentItemViewNodes,
        selectedProjectElementNodeId,
        familyAddedProductTypes,
    } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
        selectedProjectElementNodeId: s.selectedProjectElementNodeId,
        familyAddedProductTypes: s.familyAddedProductTypes,
    }))
    /** the element selected on Element Tree */
    const {
        elementId: familyElementId,
        parentFamilyId,
        isProductTypeProject,
        elementType,
    } = useElementItemShallowSelector((s) => s.elementItemValue)
    /** project id from url */
    const { projectId } = useParams<{ projectId?: string }>()
    /** determine the element id based on project type */
    const elementId = (isProductTypeProject
        ? parentFamilyId
        : familyElementId) as string
    /** Save operation tracking for drag-drop prevention */
    const { setSaveOperationInProgress } = useNodeMapActions()

    /**
     * Async mutation to update the content item element.
     * Returns the mutation response which includes the updated contentItemViewNodes.
     *
     * @returns Mutation result containing updated contentItemViewNodes
     */
    const mutateContentElements = useCallback(
        async (
            contentItemId: string,
            changedElements: Partial<TextContentElement>[],
            shouldReplaceWholeElements?: boolean,
            isNewElementAfterKeyedElement?: boolean,
            blurStartActiveTab?: number
        ) => {
            // Capture activeTab BEFORE mutation for race condition prevention
            mutationInitialActiveTabRef.current =
                blurStartActiveTab ?? useWorkspace2Store.getState().activeTab

            // Read nodes at call time so that elements inserted after the last render
            // (e.g. a newly added UOM via Insert UOM) are included in the payload.
            const currentContentItemViewNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            // build input variables for mutation
            const input = new ContentItemInputBuilder({
                contentItemId,
                changedElements,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes: currentContentItemViewNodes,
                projectId,
                familyAddedProductTypes,
                elementType: elementType as ProjectElementType,
                shouldReplaceWholeElements,
                isNewElementAfterKeyedElement,
            })

            const variables = input.build()

            // Set save operation in progress for drag-drop prevention
            setSaveOperationInProgress(true)

            try {
                // Return mutation response so caller can use the contentItemViewNodes
                const result = await updateProjectContentItemElement({
                    variables,
                })
                return result
            } finally {
                // Always clear the flag when mutation completes
                setTimeout(() => setSaveOperationInProgress(false), 100)
            }
        },
        [
            updateProjectContentItemElement,
            elementId,
            selectedProjectElementNodeId,
            projectId,
            familyAddedProductTypes,
            setSaveOperationInProgress,
        ]
    )

    /** Async mutation to update the content item element */
    const mutateNoteAsync = useCallback(
        async (
            contentItemId: string,
            specifierNotes: string[],
            isForArticle: boolean = false
        ) => {
            // build input variables for mutation
            const input = new SpecifierNotesInputBuilder({
                contentItemId,
                specifierNotes,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
                isForArticle,
            })

            const variables = input.build()

            return await updateProjectContentItemElement({
                variables,
            })
        },
        [
            updateProjectContentItemElement,
            elementId,
            selectedProjectElementNodeId,
            contentItemViewNodes,
            projectId,
        ]
    )

    return {
        mutateContentElements,
        mutateNoteAsync,
    }
}
